import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { FiBriefcase, FiMapPin, FiDollarSign, FiUsers, FiWifi, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import LLMSelector from './LLMSelector';

const JobPostForm = ({ onGenerate, isGenerating, onClear }) => {
  const [selectedModel, setSelectedModel] = useState('gpt-4');
  const [inputMode, setInputMode] = useState('title'); // 'title' or 'description'

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset
  } = useForm({
    defaultValues: {
      jobTitle: '',
      jobDescription: '',
      salaryRange: '',
      location: '',
      seniority: '',
      workMode: 'hybrid',
      notes: ''
    }
  });

  const watchedValues = watch();
  const hasContent = inputMode === 'title' ? watchedValues.jobTitle?.trim() : watchedValues.jobDescription?.trim();

  const onSubmit = (data) => {
    const formData = {
      ...data,
      selectedModel,
      inputMode
    };
    onGenerate(formData);
  };

  const handleClear = () => {
    reset();
    onClear();
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <h2 className="text-xl font-semibold flex items-center">
          <SafeIcon icon={FiBriefcase} className="mr-2" />
          Job Details
        </h2>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* Input Mode Toggle */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Input Method
          </label>
          <div className="flex rounded-lg border border-gray-300 p-1 bg-gray-50">
            <button
              type="button"
              onClick={() => setInputMode('title')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                inputMode === 'title'
                  ? 'bg-white text-primary-700 shadow-sm border border-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Job Title Only
            </button>
            <button
              type="button"
              onClick={() => setInputMode('description')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                inputMode === 'description'
                  ? 'bg-white text-primary-700 shadow-sm border border-gray-200'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Full Description
            </button>
          </div>
        </div>

        {/* Conditional Input Fields */}
        {inputMode === 'title' ? (
          <div className="space-y-2">
            <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">
              Job Title *
            </label>
            <input
              id="jobTitle"
              type="text"
              {...register('jobTitle', { 
                required: inputMode === 'title' ? 'Job title is required' : false 
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="e.g., Parks Operator II"
            />
            {errors.jobTitle && (
              <p className="text-sm text-red-600">{errors.jobTitle.message}</p>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            <label htmlFor="jobDescription" className="block text-sm font-medium text-gray-700">
              Job Description *
            </label>
            <textarea
              id="jobDescription"
              rows={6}
              {...register('jobDescription', { 
                required: inputMode === 'description' ? 'Job description is required' : false 
              })}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
              placeholder="Paste your existing job description here..."
            />
            {errors.jobDescription && (
              <p className="text-sm text-red-600">{errors.jobDescription.message}</p>
            )}
          </div>
        )}

        {/* Optional Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="salaryRange" className="block text-sm font-medium text-gray-700">
              <SafeIcon icon={FiDollarSign} className="inline mr-1" />
              Salary Range
            </label>
            <input
              id="salaryRange"
              type="text"
              {...register('salaryRange')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="e.g., $69,347.20 - 86,694.40"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="location" className="block text-sm font-medium text-gray-700">
              <SafeIcon icon={FiMapPin} className="inline mr-1" />
              Location
            </label>
            <input
              id="location"
              type="text"
              {...register('location')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
              placeholder="e.g., Metropolis, AB"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label htmlFor="seniority" className="block text-sm font-medium text-gray-700">
              <SafeIcon icon={FiUsers} className="inline mr-1" />
              Seniority Level
            </label>
            <select
              id="seniority"
              {...register('seniority')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              <option value="">Select level</option>
              <option value="entry">Entry Level</option>
              <option value="mid">Mid Level</option>
              <option value="senior">Senior Level</option>
              <option value="lead">Lead/Principal</option>
              <option value="executive">Executive</option>
            </select>
          </div>

          <div className="space-y-2">
            <label htmlFor="workMode" className="block text-sm font-medium text-gray-700">
              <SafeIcon icon={FiWifi} className="inline mr-1" />
              Work Mode
            </label>
            <select
              id="workMode"
              {...register('workMode')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              <option value="remote">Remote</option>
              <option value="onsite">On-site</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
            Additional Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register('notes')}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
            placeholder="Any additional requirements or notes..."
          />
        </div>

        {/* LLM Selector */}
        <LLMSelector 
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
        />

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4">
          <motion.button
            whileHover={{ scale: hasContent && !isGenerating ? 1.02 : 1 }}
            whileTap={{ scale: hasContent && !isGenerating ? 0.98 : 1 }}
            type="submit"
            disabled={!hasContent || isGenerating}
            className="flex-1 flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <SafeIcon icon={FiRefreshCw} className="mr-2 h-4 w-4" />
                Generate Job Posting
              </>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleClear}
            className="px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
          >
            <SafeIcon icon={FiTrash2} className="h-4 w-4" />
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default JobPostForm;